class NousTrouver extends HTMLElement {
  constructor() {
    super();
    this.markersArray = []; // Initialisation du tableau de marqueurs
    this.storeList = this.querySelector("#store-list"); // Initialisation de storeList
    this.isProgrammaticMove = false; // Ajout d'un flag pour savoir si le mouvement est programmatique

    this.init();
  }

  init() {
    this.initMap();
    this.initMarkers();
    this.initListSync();
    this.initSearch();
    this.initStoreTypeFilter();
    this.initClearButton();
  }

  initMap() {
    // Initialisation de la carte centrée sur Paris avec options minimalistes
    this.map = L.map("map", {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: false,
      keyboard: false,
      dragging: true,
      touchZoom: true,
    }).setView([48.8566, 2.3522], 13);

    // Ajout du fond de carte avec un style minimaliste
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      maxZoom: 19,
      subdomains: "abcd",
    }).addTo(this.map);

    // Ajout des contrôles de zoom personnalisés
    L.control
      .zoom({
        position: "bottomright",
      })
      .addTo(this.map);
  }

  initMarkers() {
    // Récupération des données des magasins depuis le JSON
    this.storesData = JSON.parse(this.querySelector('script[type="application/ld+json"]').textContent);

    // Création d'un groupe de marqueurs avec clustering
    this.markers = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 16,
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="cluster-count">${count}</div>`,
          className: "tr-flex tr-items-center tr-justify-center tr-w-[30px] tr-h-[30px]",
          iconSize: [30, 30],
        });
      },
    });

    // Création d'une icône personnalisée
    const customIcon = L.icon({
      iconUrl:
        "data:image/svg+xml;base64," +
        btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#351D1D">
        <path fill-rule="evenodd" d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" clip-rule="evenodd" />
      </svg>`),
      iconSize: [24, 24],
      iconAnchor: [12, 24],
      popupAnchor: [0, 0],
    });

    // Ajout des marqueurs pour chaque magasin
    this.storesData.forEach((store) => {
      if (store.latitude && store.longitude) {
        const marker = L.marker([parseFloat(store.latitude), parseFloat(store.longitude)], {
          icon: customIcon,
          keyboard: false,
          title: store.title,
        });

        // Stockage des données du magasin dans le marqueur
        marker.storeData = store;
        marker.storeIndex = store.id;

        // Création du popup avec les données stockées
        marker.bindPopup(
          `
            <div class="tr-p-0">
              <h3 class="tr-font-heading tr-text-darklava tr-mb-2" style="font-size: 18px;">${marker.storeData.title}</h3>
              <div class="tr-text-[16px] tr-text-darklava/70">${marker.storeData.address}</div>
            </div>
          `,
          {
            className: "custom-popup",
            closeButton: false,
            offset: [0, -12],
            maxWidth: 300,
          }
        );

        // Ajout du zoom au clic sur le marqueur
        marker.on("click", () => {
          this.map.setView([parseFloat(marker.storeData.latitude), parseFloat(marker.storeData.longitude)], 16, {
            animate: true,
            duration: 1,
          });
          // Mise en évidence de la boutique dans la liste
          this.highlightStore(marker.storeIndex);
        });

        this.markersArray.push(marker);
        this.markers.addLayer(marker);
      }
    });

    // Ajout des marqueurs à la carte
    this.markers.addTo(this.map);

    // Ajustement de la vue pour montrer tous les marqueurs
    if (this.markersArray.length > 0) {
      this.map.fitBounds(this.markers.getBounds(), {
        padding: [50, 50],
        maxZoom: 15,
      });
    }
  }

  initListSync() {
    // Écoute des changements de vue de la carte
    this.map.on("moveend", () => {
      if (this.isProgrammaticMove) {
        this.isProgrammaticMove = false;
        return;
      }
      this.updateVisibleStores();
    });

    // Initialisation de la liste
    this.updateVisibleStores();

    // Ajout des événements de clic sur les éléments de la liste principale uniquement sur desktop
    const storeList = this.querySelector("#store-list");
    if (storeList) {
      const storeItems = storeList.querySelectorAll("li");
      const isDesktop = window.innerWidth >= 1024;
      storeItems.forEach((item, index) => {
        item.style.cursor = isDesktop ? "pointer" : "default";
        if (isDesktop) {
          item.addEventListener("click", () => {
            const marker = this.markersArray[index];
            if (marker) {
              this.zoomToMarker(marker, index);
            }
          });
        }
      });
    }

    // Ajout des événements de clic sur les éléments de la liste rapide
    const quickSelectList = this.querySelector("#pill-list-quick-select");
    if (quickSelectList) {
      const quickSelectItems = quickSelectList.querySelectorAll("li");
      quickSelectItems.forEach((item, index) => {
        item.addEventListener("click", () => {
          const marker = this.markersArray[index];
          if (marker) {
            this.zoomToMarker(marker, index);
            // Mise en évidence de l'élément dans la liste rapide
            quickSelectItems.forEach((pill) => pill.classList.remove("active"));
            item.classList.add("active");
          }
        });
        item.style.cursor = "pointer";
      });
    }
  }

  zoomToMarker(marker, index) {
    const latLng = marker.getLatLng();

    // Calcul du padding pour un meilleur centrage
    const mapSize = this.map.getSize();
    const padding = {
      top: mapSize.y * 0.1, // 10% du haut
      bottom: mapSize.y * 0.1, // 10% du bas
      left: mapSize.x * 0.1, // 10% de la gauche
      right: mapSize.x * 0.1, // 10% de la droite
    };

    this.isProgrammaticMove = true;
    // Zoom et centrage sur le marqueur avec padding
    this.map.setView(latLng, 16, {
      animate: true,
      duration: 1,
      padding: [padding.top, padding.right, padding.bottom, padding.left],
      script: true,
    });

    // Ouvre le popup
    marker.openPopup();

    // Mise en évidence de l'élément dans la liste principale
    this.highlightStore(index);
  }

  updateVisibleStores() {
    const bounds = this.map.getBounds();
    const storeList = this.querySelector("#store-list");

    // Vérifier s'il y a des filtres actifs
    const searchInput = this.querySelector(".search__input");
    const searchTerm = searchInput ? searchInput.value.toLowerCase() : "";
    const selectedType = document.querySelector('input[name="boutique"]:checked')?.value;
    const hasActiveFilters = searchTerm !== "" || selectedType;

    // Si des filtres sont actifs, on ne met pas à jour la liste selon la vue
    if (hasActiveFilters) {
      console.log("Filtres actifs détectés, updateVisibleStores ignoré");
      return;
    }

    if (storeList) {
      const storeItems = storeList.querySelectorAll("li");
      let visibleCount = 0;

      storeItems.forEach((item, index) => {
        const marker = this.markersArray[index];
        if (marker) {
          const latLng = marker.getLatLng();
          const isVisible = latLng.lat >= bounds.getSouth() && latLng.lat <= bounds.getNorth() && latLng.lng >= bounds.getWest() && latLng.lng <= bounds.getEast();

          item.style.display = isVisible ? "flex" : "none";
          if (isVisible) visibleCount++;
        }
      });

      // Gestion du message "aucun magasin"
      let noStoreMessage = storeList.querySelector(".no-store-message");
      if (visibleCount === 0) {
        if (!noStoreMessage) {
          noStoreMessage = document.createElement("li");
          noStoreMessage.className = "no-store-message tr-text-center tr-py-8 tr-text-darklava/70";
          noStoreMessage.textContent = "Aucun magasin Yade ou revendeur dans ce secteur";
          storeList.appendChild(noStoreMessage);
        }
        noStoreMessage.style.display = "block";
      } else if (noStoreMessage) {
        noStoreMessage.style.display = "none";
      }
    }
  }

  highlightStore(index) {
    const storeList = this.querySelector("#store-list");
    if (storeList) {
      const storeItems = storeList.querySelectorAll("li");
      storeItems.forEach((item) => {
        const itemIndex = parseInt(item.dataset.storeId);
        if (itemIndex === index) {
          item.classList.add("tr-bg-[#F5F5F5]");
          item.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          item.classList.remove("tr-bg-[#F5F5F5]");
        }
      });
    }
  }

  initSearch() {
    const searchInput = this.querySelector(".search__input");
    const searchForm = this.querySelector("form");

    if (searchForm) {
      searchForm.addEventListener("submit", (e) => {
        e.preventDefault();
        // Fermer le clavier virtuel sur mobile
        if (searchInput) {
          searchInput.blur();
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        const searchTerm = e.target.value.toLowerCase();
        this.filterStores(searchTerm);
        this.toggleClearButton();
      });

      // Ajouter la gestion de la touche Entrée
      searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          searchInput.blur();
        }
      });
    }
  }

  initStoreTypeFilter() {
    const radioButtons = this.querySelectorAll('input[name="boutique"]');
    radioButtons.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedType = document.querySelector('input[name="boutique"]:checked')?.value;
        this.filterByStoreType(selectedType);
        this.toggleClearButton();
      });
    });
  }

  filterByStoreType(storeType) {
    if (!this.storeList) return;

    const storeItems = this.storeList.querySelectorAll("li");
    let visibleCount = 0;
    let visibleMarkers = [];

    // Récupérer aussi le terme de recherche actuel
    const searchInput = this.querySelector(".search__input");
    const searchTerm = searchInput ? searchInput.value.toLowerCase() : "";

    storeItems.forEach((item) => {
      const storeIndex = item.dataset.storeId;
      if (storeIndex === undefined) {
        item.style.display = "none";
        return;
      }
      const index = parseInt(storeIndex, 10);
      const marker = this.markersArray[index];
      const storeData = marker ? marker.storeData : null;

      if (storeData) {
        const title = storeData.title.toLowerCase();
        const address = storeData.address.toLowerCase();
        const ville = storeData.ville ? storeData.ville.toLowerCase() : "";

        // Les deux conditions combinées (recherche ET type)
        const matchesSearch = searchTerm === "" || title.includes(searchTerm) || address.includes(searchTerm) || ville.includes(searchTerm);
        const matchesType = !storeType || storeData.store_type === storeType;
        const isVisible = matchesSearch && matchesType;

        item.style.display = isVisible ? "flex" : "none";

        if (isVisible && marker) {
          visibleCount++;
          visibleMarkers.push(marker);
        }
      }
    });

    // Gestion du message "aucun magasin"
    let noStoreMessage = this.storeList.querySelector(".no-store-message");
    if (visibleCount === 0) {
      if (!noStoreMessage) {
        noStoreMessage = document.createElement("li");
        noStoreMessage.className = "no-store-message tr-text-center tr-py-8 tr-text-darklava/70";
        noStoreMessage.textContent = "Aucun magasin trouvé pour ce type";
        this.storeList.appendChild(noStoreMessage);
      }
      noStoreMessage.style.display = "block";
    } else if (noStoreMessage) {
      noStoreMessage.style.display = "none";
    }

    // Ajustement de la vue de la carte SEULEMENT sur les marqueurs qui correspondent aux deux filtres
    if (visibleMarkers.length > 0) {
      this.isProgrammaticMove = true;
      const group = L.featureGroup(visibleMarkers);
      this.map.fitBounds(group.getBounds(), {
        padding: [50, 50],
        maxZoom: 16,
        animate: true,
        duration: 1,
      });
    }
  }

  filterStores(searchTerm) {
    const storeList = this.querySelector("#store-list");
    if (!storeList) return;

    const storeItems = storeList.querySelectorAll("li");
    let visibleCount = 0;
    let visibleMarkers = [];
    // On récupère le type sélectionné (peut être undefined)
    const selectedType = document.querySelector('input[name="boutique"]:checked')?.value;

    storeItems.forEach((item, itemIndex) => {
      const storeIndex = item.dataset.storeId;
      if (storeIndex === undefined) {
        item.style.display = "none";
        return;
      }
      const index = parseInt(storeIndex, 10);
      const marker = this.markersArray[index];
      const storeData = marker ? marker.storeData : null;

      if (storeData) {
        const title = storeData.title.toLowerCase();
        const address = storeData.address.toLowerCase();
        const ville = storeData.ville ? storeData.ville.toLowerCase() : "";

        // Les deux conditions combinées
        const matchesSearch = searchTerm === "" || title.includes(searchTerm) || address.includes(searchTerm) || ville.includes(searchTerm);
        const matchesType = !selectedType || storeData.store_type === selectedType;
        const isVisible = matchesSearch && matchesType;

        item.style.display = isVisible ? "flex" : "none";

        // Debug spécifique pour la boutique Yade
        if (storeData.title.includes("Yade")) {
          console.log(`Yade après application du style: display = ${item.style.display}`);
        }

        if (isVisible && marker) {
          visibleCount++;
          visibleMarkers.push(marker);
        }
      }
    });

    // Gestion du message "aucun magasin"
    let noStoreMessage = storeList.querySelector(".no-store-message");
    if (visibleCount === 0) {
      if (!noStoreMessage) {
        noStoreMessage = document.createElement("li");
        noStoreMessage.className = "no-store-message tr-text-center tr-py-8 tr-text-darklava/70";
        noStoreMessage.textContent = "Aucun magasin trouvé";
        storeList.appendChild(noStoreMessage);
      }
      noStoreMessage.style.display = "block";
    } else if (noStoreMessage) {
      noStoreMessage.style.display = "none";
    }

    // Ajustement de la vue de la carte
    if (visibleMarkers.length > 0) {
      const group = L.featureGroup(visibleMarkers);
      this.map.fitBounds(group.getBounds(), {
        padding: [50, 50],
        maxZoom: 16,
        animate: true,
        duration: 1,
      });
    }
  }

  initClearButton() {
    const clearButton = this.querySelector('button[aria-label="Effacer la recherche"]');
    if (clearButton) {
      // Masquer le bouton par défaut
      this.toggleClearButton();

      clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }
  }

  toggleClearButton() {
    const clearButton = this.querySelector('button[aria-label="Effacer la recherche"]');
    const searchInput = this.querySelector(".search__input");
    const searchTerm = searchInput ? searchInput.value.toLowerCase() : "";
    const selectedType = document.querySelector('input[name="boutique"]:checked')?.value;
    const hasActiveFilters = searchTerm !== "" || selectedType;

    if (clearButton) {
      clearButton.style.display = hasActiveFilters ? "block" : "none";
    }
  }

  clearAllFilters() {
    // Vider la recherche
    const searchInput = this.querySelector(".search__input");
    if (searchInput) {
      searchInput.value = "";
    }

    // Décocher les radios
    const radioButtons = this.querySelectorAll('input[name="boutique"]');
    radioButtons.forEach(radio => radio.checked = false);

    // Réafficher tous les magasins
    this.showAllStores();

    // Masquer le bouton de réinitialisation
    this.toggleClearButton();
  }

  showAllStores() {
    if (!this.storeList) return;

    const storeItems = this.storeList.querySelectorAll("li");
    storeItems.forEach((item) => {
      const storeIndex = item.dataset.storeId;
      if (storeIndex !== undefined) {
        item.style.display = "flex";
      }
    });

    // Masquer le message "aucun magasin"
    const noStoreMessage = this.storeList.querySelector(".no-store-message");
    if (noStoreMessage) {
      noStoreMessage.style.display = "none";
    }

    // Réajuster la carte pour montrer tous les marqueurs
    if (this.markersArray.length > 0) {
      this.isProgrammaticMove = true;
      this.map.fitBounds(this.markers.getBounds(), {
        padding: [50, 50],
        maxZoom: 15,
      });
    }
  }
}

customElements.define("nous-trouver", NousTrouver);
